import type { RelatorioDTO } from "@caint/shared";
import { descricaoNivel, nivelValido } from "../../lib/escalaMaturidade.ts";
import "../../styles/relatorio-institucional.css";

/**
 * Relatório Preliminar de Avaliação de Maturidade Institucional (Direção da
 * Unidade) — render fiel ao documento formal da CAINT/EMSERH, populado com o
 * DTO autoritativo do backend (`GET /relatorio`). Layout pensado para A4: a
 * emissão em PDF é a impressão do navegador sobre `.rel-inst-print`.
 *
 * Componente PURO de apresentação — não busca dados nem calcula nada; recebe o
 * DTO já montado no servidor (single source of truth).
 */

const pct = (v: number | null): string => (v == null ? "—" : `${(v * 100).toFixed(1).replace(".", ",")}%`);

const perc = (n: number, total: number): string =>
  total > 0 ? `${((n / total) * 100).toFixed(1).replace(".", ",")}%` : "—";

const dataBR = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const STATUS_ROTULO: Record<string, string> = {
  pendente: "Pendente",
  em_andamento: "Em andamento",
  concluido: "Concluído",
  rejeitado: "Rejeitado",
};

const EIXO_NOME: Record<string, string> = {
  IMGO: "Gestão Operacional",
  IMQA: "Qualidade Assistencial",
  IMGR: "Gestão de Riscos",
};

const TOLERANCIA_RISCO: Record<string, string> = {
  rp: "Sem necessidade de tratamento e Plano de Ação.",
  rm: "Deve ser monitorado, com possíveis Planos de Ação.",
  ra: "Resposta a médio prazo com Plano de Ação (prazo pode ser avaliado pelo gestor).",
  rc: "Resposta imediata — atuação proativa e urgente do gestor.",
};

/**
 * Sobreposição de emissão: barra de ações (`.no-print`) com Imprimir/PDF e
 * Fechar, e o relatório imprimível abaixo. Reutilizada pelo Portal da Unidade e
 * pelas telas internas — a impressão revela só `.rel-inst-print`.
 */
export function EmitirRelatorioOverlay({ dto, onFechar }: { dto: RelatorioDTO; onFechar: () => void }) {
  return (
    <div className="ri-overlay">
      <div className="ri-toolbar no-print">
        <button type="button" className="m-btn primary" onClick={() => window.print()}>
          🖨 Imprimir / PDF
        </button>
        <button type="button" className="m-btn" onClick={onFechar}>
          Fechar
        </button>
      </div>
      <div className="ri-folha">
        <RelatorioInstitucional dto={dto} />
      </div>
    </div>
  );
}

export interface RelatorioInstitucionalProps {
  dto: RelatorioDTO;
}

/**
 * Situação encontrada = a nota de maturidade que originou o item, por extenso.
 * É o FATO do FACR. `—` em item manual, que não nasce de uma nota.
 */
function situacaoDoItem(maturidade: string | null): string {
  const n = nivelValido(maturidade);
  return n ? `${n} · ${descricaoNivel(n).situacao}` : "—";
}

export function RelatorioInstitucional({ dto }: RelatorioInstitucionalProps) {
  const { meta, identificacao: id, imgq, ona, riscos, plano, audin, percepcao, consideracoes: cons } = dto;
  const eixoFragil = [...dto.subindices]
    .filter((e) => e.score != null)
    .sort((a, b) => (a.score ?? 1) - (b.score ?? 1))[0];
  const totalRisco = riscos.rp + riscos.rm + riscos.ra + riscos.rc;
  // Rótulos derivados do ciclo (backend): 1º ciclo = PRELIMINAR; 2º+ = FINAL.
  const ehFinal = meta.tipo === "final";
  const rotuloDoc = ehFinal ? "FINAL" : "PRELIMINAR";
  const ordinal = `${meta.cicloNumero}º Ciclo`;
  // Só itens com FATO preenchido entram no Plano de Ação impresso: os classificados
  // como NC/PC sem análise ainda ficam com Fato em branco e são ocultados.
  // O Fato deixou de ser uma COLUNA da tabela (só Item e Resultado são impressos),
  // mas segue sendo o critério de entrada: item sem análise da não conformidade não
  // é item de plano publicável. Não remover este filtro junto com a coluna.
  const planoComFato = plano.itens.filter((it) => (it.fato ?? "").trim() !== "");

  return (
    <div className="rel-inst-print ri-doc">
      {/* Cabeçalho */}
      <header className="ri-cabecalho">
        <h1>RELATÓRIO {rotuloDoc} DE AVALIAÇÃO DE MATURIDADE INSTITUCIONAL</h1>
        <p className="ri-sub">Índice de Maturidade da Gestão e Qualidade (IMGQ)</p>
        <p className="ri-sub ri-italic">
          Comissão para Avaliação Interna da Maturidade de Unidades de Saúde (CAINT) — EMSERH
        </p>
        <div className="ri-aviso">
          <strong>
            DOCUMENTO {rotuloDoc} — {ordinal.toUpperCase()} DO PROCESSO AVALIATIVO.
          </strong>{" "}
          Apresenta os resultados {ehFinal ? "consolidados" : "diagnósticos"} do {ordinal} de avaliação da
          unidade, conduzido pela CAINT/EMSERH ({meta.portaria}).{" "}
          {ehFinal
            ? "Inclui a verificação da efetividade das medidas corretivas e a validação final pela AUDIN, base para a atribuição do Selo EMSERH de Maturidade."
            : "Os dados e classificações são preliminares e de caráter diagnóstico: a atribuição do Selo EMSERH de Maturidade é ato exclusivo do Relatório Final, ao término do 2º Ciclo."}{" "}
          Uso administrativo deste documento deve observar seu caráter formativo, não punitivo.
        </div>
      </header>

      {/* 1 · Identificação */}
      <section className="ri-sec">
        <h2>1. Identificação da unidade e do ciclo avaliativo</h2>
        <table className="ri-tab ri-tab-id">
          <tbody>
            <tr>
              <th>Unidade de saúde</th>
              <td>{id.unidadeNome}</td>
            </tr>
            <tr>
              <th>Município/UF</th>
              <td>
                {id.municipio}/{id.uf}
              </td>
            </tr>
            <tr>
              <th>CNES</th>
              <td>{id.cnes ?? "—"}</td>
            </tr>
            <tr>
              <th>Mantenedora</th>
              <td>{id.mantenedora}</td>
            </tr>
            <tr>
              <th>Comissão responsável</th>
              <td>{id.comissao}</td>
            </tr>
            <tr>
              <th>Escopo do ciclo</th>
              <td>
                {id.escopoSetores} setor(es) avaliados · {id.escopoItensRespondidos} itens respondidos
              </td>
            </tr>
            <tr>
              <th>Início do ciclo</th>
              <td>{dataBR(id.inicioCiclo)}</td>
            </tr>
            <tr>
              <th>Natureza do relatório</th>
              <td>
                {meta.numero ? `Nº ${meta.numero}` : "Nº ____/2026"} — {meta.natureza}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 2 · Sumário executivo */}
      <section className="ri-sec">
        <h2>2. Sumário executivo</h2>
        <p>
          A unidade apresenta <strong>IMGQ efetivo de {pct(imgq.efetivo)}</strong> (bruto {pct(imgq.bruto)}),
          selo <strong>{imgq.seloRotulo || "—"}</strong>
          {imgq.seloNivel ? ` (${imgq.seloNivel})` : ""}.{" "}
          {imgq.travado
            ? "O selo está travado pela regra de exclusão CORE (itens críticos em nível 0/1 ou eixo abaixo da meta)."
            : "A regra de exclusão CORE não está acionada."}{" "}
          {eixoFragil
            ? `O eixo mais frágil é ${eixoFragil.eixo} — ${EIXO_NOME[eixoFragil.eixo]} (${pct(eixoFragil.score)}).`
            : ""}{" "}
          Há <strong>{plano.total} item(ns)</strong> no Plano de Ação, dos quais {riscos.plano15} de risco
          crítico (prazo de 15 dias).
        </p>
      </section>

      {/* 3 · Fundamentação */}
      <section className="ri-sec">
        <h2>3. Fundamentação metodológica e normativa</h2>
        <ul className="ri-lista">
          {dto.fundamentacao.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      </section>

      {/* 4 · Subíndices por eixo */}
      <section className="ri-sec">
        <h2>4. Resultados gerais — subíndices por eixo</h2>
        <table className="ri-tab">
          <thead>
            <tr>
              <th>Eixo</th>
              <th>Descrição</th>
              <th className="ri-num">Score</th>
              <th className="ri-num">Itens</th>
            </tr>
          </thead>
          <tbody>
            {dto.subindices.map((e) => (
              <tr key={e.eixo}>
                <td>
                  <strong>{e.eixo}</strong>
                </td>
                <td>{e.nome}</td>
                <td className="ri-num">
                  <strong>{pct(e.score)}</strong>
                </td>
                <td className="ri-num">{e.nItens}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 5 · Resultados por setor */}
      <section className="ri-sec">
        <h2>5. Resultados por setor</h2>
        <table className="ri-tab">
          <thead>
            <tr>
              <th>Setor</th>
              <th className="ri-num">Score</th>
              <th className="ri-num">Itens</th>
              <th className="ri-num">CORE≤1</th>
              <th className="ri-num">NC</th>
              <th className="ri-num">Plano</th>
            </tr>
          </thead>
          <tbody>
            {dto.setores.map((s) => (
              <tr key={s.code}>
                <td>
                  {s.code} · {s.nome}
                </td>
                <td className="ri-num">
                  <strong>{pct(s.score)}</strong>
                </td>
                <td className="ri-num">{s.nItens}</td>
                <td className="ri-num">{s.coreCriticos}</td>
                <td className="ri-num">{s.nc}</td>
                <td className="ri-num">{s.noPlano}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 6 · Curva ONA */}
      <section className="ri-sec">
        <h2>6. Análise de conformidade (curva ONA)</h2>
        <table className="ri-tab">
          <thead>
            <tr>
              <th>Classificação</th>
              <th className="ri-num">Itens</th>
              <th className="ri-num">% do avaliado</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Não Conforme (NC)</td>
              <td className="ri-num">{ona.nc}</td>
              <td className="ri-num">{perc(ona.nc, ona.totalAvaliado)}</td>
            </tr>
            <tr>
              <td>Parcialmente Conforme (PC)</td>
              <td className="ri-num">{ona.pc}</td>
              <td className="ri-num">{perc(ona.pc, ona.totalAvaliado)}</td>
            </tr>
            <tr>
              <td>Conforme (C + CS)</td>
              <td className="ri-num">{ona.c + ona.cs}</td>
              <td className="ri-num">{perc(ona.c + ona.cs, ona.totalAvaliado)}</td>
            </tr>
            <tr>
              <td>Não Aplicável (NA)</td>
              <td className="ri-num">{ona.na}</td>
              <td className="ri-num">—</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th>Total avaliado</th>
              <th className="ri-num">{ona.totalAvaliado}</th>
              <th className="ri-num">100%</th>
            </tr>
          </tfoot>
        </table>
      </section>

      {/* 7 · Gestão de riscos */}
      <section className="ri-sec">
        <h2>7. Gestão de riscos</h2>
        <p>
          A avaliação cruza risco inerente (probabilidade × impacto) e risco residual, evidenciando o efeito
          da maturidade dos controles. Distribuição dos itens por risco residual:
        </p>
        <table className="ri-tab">
          <thead>
            <tr>
              <th>Classificação de risco</th>
              <th>Tolerância</th>
              <th className="ri-num">Itens</th>
              <th className="ri-num">%</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                ["RP — Risco Pequeno (1–3)", "rp", riscos.rp],
                ["RM — Risco Moderado (4–6)", "rm", riscos.rm],
                ["RA — Risco Alto (8–12)", "ra", riscos.ra],
                ["RC — Risco Crítico (15–25)", "rc", riscos.rc],
              ] as const
            ).map(([rotulo, chave, n]) => (
              <tr key={chave}>
                <td>
                  <strong>{rotulo}</strong>
                </td>
                <td>{TOLERANCIA_RISCO[chave]}</td>
                <td className="ri-num">{n}</td>
                <td className="ri-num">{perc(n, totalRisco)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* 8 · Plano de Ação FACR */}
      <section className="ri-sec">
        <h2>8. Plano de Ação FACR — itens prioritários</h2>
        {planoComFato.length === 0 ? (
          <p>
            {plano.total === 0
              ? "Nenhum item-ação registrado neste ciclo."
              : `Nenhum dos ${plano.total} item(ns) do plano tem Fato registrado ainda — o Fato é preenchido pela equipe após a análise das não conformidades.`}
          </p>
        ) : (
          <>
            <table className="ri-tab ri-tab-plano">
              <thead>
                <tr>
                  <th>Item (setor · código · descrição)</th>
                  {/* O FATO do FACR: a nota que fez o item existir. A tabela
                      mostrava só a pergunta, então faltava o "o que foi
                      encontrado" — mesma lacuna corrigida no Portal da Unidade. */}
                  <th>Situação encontrada</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {planoComFato.map((it) => (
                  <tr key={it.code}>
                    <td>
                      <div className="ri-plano-cod">
                        {it.setor} · {it.code}
                        {it.obrigatorio ? " ★" : ""}
                      </div>
                      <div className="ri-plano-desc">
                        {[it.setorNome, it.pergunta].filter(Boolean).join(" — ")}
                      </div>
                    </td>
                    <td>{situacaoDoItem(it.maturidadeGatilho)}</td>
                    <td>{STATUS_ROTULO[it.status] ?? it.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="ri-nota">
              ★ item obrigatório (CORE). Ordenado por gravidade — pior situação primeiro, CORE à frente: é a
              mesma ordem em que a unidade encontra os itens no Portal do Plano de Ação. Exibindo{" "}
              {planoComFato.length} de {plano.total} item(ns) — ocultos os que ainda não têm a análise da não
              conformidade registrada.
            </p>
          </>
        )}
      </section>

      {/* 9 · Supervisão AUDIN */}
      <section className="ri-sec">
        <h2>9. Supervisão AUDIN</h2>
        {audin?.temDados ? (
          <p>
            Parecer de confiabilidade: <strong>{audin.parecer ?? "—"}</strong>
            {audin.taxaCorroboracao != null
              ? `, com corroboração de ${pct(audin.taxaCorroboracao)} da amostra cega re-executada`
              : ""}
            . {audin.ncAltaCriticidade != null ? `${audin.ncAltaCriticidade}` : "—"} não conformidade(s) de
            alta criticidade identificada(s), incorporada(s) ao Plano de Ação.
          </p>
        ) : (
          <p>Supervisão AUDIN ainda não registrada para esta avaliação.</p>
        )}
      </section>

      {/* 10 · Percepção interna (questionário público do Manual IMGR) */}
      {percepcao ? (
        <section className="ri-sec">
          <h2>10. Percepção interna de riscos (questionário IMGR)</h2>
          <p>
            Questionário do Manual IMGR (31 afirmativas em 6 componentes, escala 0–3), respondido de forma
            anônima e voluntária pela equipe da unidade durante o ciclo:{" "}
            <strong>{percepcao.nRespondentes} respondente(s)</strong> ({percepcao.nGestores} gestor(es) e{" "}
            {percepcao.nLiderados} liderado(s)). Resultado:{" "}
            <strong>
              {percepcao.imgr == null ? "—" : `${percepcao.imgr.toFixed(1).replace(".", ",")} / 100`}
            </strong>
            {percepcao.faixa ? ` — faixa ${percepcao.faixa}` : ""}.
          </p>
          <table className="ri-tab">
            <thead>
              <tr>
                <th>Componente</th>
                <th className="ri-num">Maturidade percebida</th>
              </tr>
            </thead>
            <tbody>
              {percepcao.componentes.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.id}</strong> · {c.nome}
                  </td>
                  <td className="ri-num">{c.pct == null ? "—" : `${c.pct.toFixed(1).replace(".", ",")}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ri-nota">
            Este resultado <strong>não integra o IMGQ</strong>: é autoavaliação de percepção, não evidência
            auditada. Consta do relatório pelo <strong>contraste</strong> com o subíndice IMGR medido no
            checklist — a distância entre o que a equipe percebe e o que a auditoria constata é, ela própria,
            um indicador de maturidade em gestão de riscos.
          </p>
          {percepcao.gapPontos != null ? (
            <p>
              <strong>Distância de percepção:</strong>{" "}
              {Math.abs(percepcao.gapPontos).toFixed(1).replace(".", ",")} pontos —{" "}
              {percepcao.gapPontos > 0
                ? "a equipe se percebe MAIS madura do que a evidência sustenta. Quanto maior a distância, maior o risco de que fragilidades reais não estejam sendo identificadas internamente (o que, no Manual IMGR, é o próprio objeto do componente C2 — Percepção dos Riscos)."
                : "a equipe se percebe MENOS madura do que a evidência sustenta, o que sugere subaproveitamento ou desconhecimento dos controles já implantados."}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* 11 · Considerações finais */}
      <section className="ri-sec">
        <h2>{percepcao ? "11" : "10"}. Considerações finais e próximos passos</h2>
        {cons.findings.map((f) => (
          <p key={f.titulo}>
            <strong>{f.titulo}.</strong> {f.texto}
          </p>
        ))}
        <p>
          A execução dos <strong>{cons.planoTotal} item(ns)</strong> do Plano de Ação — com prioridade aos{" "}
          {cons.riscoCritico} de risco crítico — deve ser conduzida em paralelo ao plano de menor esforço do
          simulador de metas ({cons.simuladorPassos} passo(s)), que projeta o IMGQ de {pct(cons.imgqInicial)}{" "}
          para {pct(cons.imgqFinal)}
          {cons.simuladorAtingiu ? ", cruzando a meta institucional" : ""}.{" "}
          {ehFinal
            ? "Este é o Relatório Final do processo avaliativo, consolidando a verificação da efetividade das medidas corretivas e a validação final pela AUDIN."
            : "Este relatório será complementado pelo Relatório Final do 2º Ciclo."}
        </p>
      </section>

      {/* 11 · Assinaturas */}
      <section className="ri-sec ri-assinaturas">
        <h2>{percepcao ? "12" : "11"}. Declarações e assinaturas</h2>
        <p className="ri-nota">
          Emitido em {dataBR(meta.emitidoEm)} · Estado da avaliação: {meta.estado}. São Luís/MA, data da
          assinatura eletrônica.
        </p>

        {/* 11.1 · Avaliadores Internos que fizeram o checklist desta unidade */}
        <h3 className="ri-subh">11.1. Avaliadores Internos</h3>
        {dto.avaliadores.length === 0 ? (
          <p className="ri-nota">Nenhum avaliador registrado para esta unidade.</p>
        ) : (
          <>
            <p>
              {dto.avaliadoresPresumidos
                ? "Quadro de Avaliadores Internos da CAINT/EMSERH — assinam os que conduziram esta avaliação, declarando que as constatações, evidências e classificações registradas neste relatório são fiéis ao verificado, com base no Checklist CAINT/EMSERH e observados os princípios de imparcialidade, responsabilidade e sigilo profissional."
                : "Os Avaliadores Internos abaixo declaram que as constatações, evidências e classificações registradas neste relatório são fiéis ao verificado durante a avaliação, conduzida com base no Checklist CAINT/EMSERH, observados os princípios de imparcialidade, responsabilidade e sigilo profissional."}
            </p>
            <div className="ri-firmas">
              {dto.avaliadores.map((a) => (
                <div className="ri-firma" key={a.nome}>
                  <div className="ri-firma-espaco">
                    {a.assinaturaUrl ? (
                      <img className="ri-firma-img" src={a.assinaturaUrl} alt={`Assinatura de ${a.nome}`} />
                    ) : null}
                  </div>
                  <div className="ri-linha-firma">____________________________________</div>
                  <div className="ri-firma-nome">{a.nome}</div>
                  <div className="ri-firma-cargo">
                    Avaliador(a) Interno(a) EMSERH
                    {a.eixo ? ` — ${EIXO_NOME[a.eixo] ?? a.eixo}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 11.2 · Supervisão AUDIN que validou */}
        {dto.supervisores.length > 0 && (
          <>
            <h3 className="ri-subh">11.2. Validação técnica — Supervisão AUDIN</h3>
            <div className="ri-firmas">
              {dto.supervisores.map((s) => (
                <div className="ri-firma" key={s.nome}>
                  <div className="ri-firma-espaco">
                    {s.assinaturaUrl ? (
                      <img className="ri-firma-img" src={s.assinaturaUrl} alt={`Assinatura de ${s.nome}`} />
                    ) : null}
                  </div>
                  <div className="ri-linha-firma">____________________________________</div>
                  <div className="ri-firma-nome">{s.nome}</div>
                  <div className="ri-firma-cargo">Supervisor(a) — Auditoria Interna (AUDIN) EMSERH</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* De acordo — Coordenação/Presidência da CAINT (constantes) */}
        <h3 className="ri-subh">De acordo — Coordenação da CAINT</h3>
        <div className="ri-firmas">
          {dto.signatarios.map((s) => (
            <div className="ri-firma" key={s.matricula}>
              <div className="ri-linha-firma">____________________________________</div>
              <div className="ri-firma-nome">{s.nome}</div>
              <div className="ri-firma-cargo">
                {s.cargo} · EMSERH — Mat. {s.matricula}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
